import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Copy, Scissors, Heart, Loader2, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const roles = [
  { id: "tech", icon: Scissors, t: "I'm a nail tech", d: "Get listed, get DM bookings, post on the board." },
  { id: "client", icon: Heart, t: "I'm a client", d: "Find techs and follow along with tips." },
];

const fieldCls = "h-12 w-full rounded-2xl border border-[#EAE4DC] bg-white px-4 text-sm outline-none focus:border-pink-400";

export const LocationFields = ({ loc, setLoc }) => (
  <div data-testid="location-fields" className="grid grid-cols-6 gap-3">
    <input data-testid="location-city-input" required minLength={2} value={loc.city} onChange={(e) => setLoc({ ...loc, city: e.target.value })} placeholder="City" className={`${fieldCls} col-span-6 sm:col-span-3`} />
    <input data-testid="location-state-input" required minLength={2} value={loc.state} onChange={(e) => setLoc({ ...loc, state: e.target.value })} placeholder="State (e.g. TX)" className={`${fieldCls} col-span-3 sm:col-span-1`} />
    <input data-testid="location-zip-input" required inputMode="numeric" pattern="\d{5}" maxLength={5} value={loc.zip} onChange={(e) => setLoc({ ...loc, zip: e.target.value.replace(/\D/g, "") })} placeholder="ZIP code" className={`${fieldCls} col-span-3 sm:col-span-2`} />
  </div>
);

const JoinForm = ({ onDone }) => {
  const [params] = useSearchParams();
  const [handle, setHandle] = useState("");
  const [role, setRole] = useState(params.get("role") === "client" ? "client" : "tech");
  const [loc, setLoc] = useState({ city: "", state: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/claims", { username: handle, role, location: role === "tech" ? loc : null }); onDone(data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-10 space-y-6 text-left">
      <label className="flex items-center gap-2 rounded-2xl border border-[#EAE4DC] bg-white px-5">
        <span className="font-mono text-stone-400">@</span>
        <input data-testid="join-handle-input" required value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="your instagram handle" autoCapitalize="none" className="h-14 w-full bg-transparent outline-none" />
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        {roles.map(({ id, icon: Icon, t, d }) => (
          <button type="button" key={id} data-testid={`role-select-${id === "tech" ? "nail-tech" : "client"}`} onClick={() => setRole(id)}
            className={`rounded-2xl border p-5 text-left transition-colors ${role === id ? "border-pink-500 bg-pink-50" : "border-[#EAE4DC] bg-white hover:border-stone-400"}`}>
            <Icon className="h-5 w-5 text-pink-500" /><p className="mt-3 font-serif text-lg">{t}</p><p className="text-xs text-stone-600">{d}</p>
          </button>
        ))}
      </div>
      {role === "tech" && (
        <div className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Where clients can find you</p>
          <LocationFields loc={loc} setLoc={setLoc} />
        </div>
      )}
      <p data-testid="public-account-note" className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800">Your Instagram must be <b>public</b> while you verify. If it's private, switch it to public for a minute, verify, then switch it back.</p>
      <button data-testid="join-submit-button" disabled={busy} className="ig-gradient w-full rounded-full py-4 text-sm font-medium text-white disabled:opacity-60">{busy ? "One sec…" : "Get my verification code"}</button>
    </form>
  );
};

export const PendingCard = ({ claim, onRestart, onVerified }) => {
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);
  const copy = async () => { try { await navigator.clipboard.writeText(claim.code); toast.success("Code copied"); } catch { /* blocked */ } };
  const check = async () => {
    setBusy(true); setHint("");
    try {
      const { data } = await api.post("/claims/check");
      if (data.verified) { toast.success("You're verified! You can remove the code from your bio now."); onVerified(); }
      else setHint(data.reason);
    } catch (e) { setHint(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="pending-card" className="mt-10 rounded-[2rem] border border-[#EAE4DC] bg-white p-8 text-left">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone-500">Your code for @{claim.username}</p>
      <button data-testid="verification-code" onClick={copy} className="mt-3 flex w-full items-center justify-between rounded-2xl bg-[#181615] px-6 py-5 font-mono text-2xl tracking-widest text-white">
        {claim.code} <Copy className="h-5 w-5 text-stone-400" />
      </button>
      <ol className="mt-6 space-y-3 text-sm text-stone-700">
        <li><b>1.</b> Copy the code above.</li>
        <li><b>2.</b> On Instagram, go to <b>Edit profile</b> and paste it anywhere in your <b>bio</b>. Save. Your account must be <b>public</b> for this step.</li>
        <li><b>3.</b> Come back and tap <b>Check my bio</b>. Afterwards you can delete the code and switch back to private.</li>
      </ol>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <a href="https://www.instagram.com/accounts/edit/" target="_blank" rel="noreferrer" onClick={copy} data-testid="open-ig-edit-profile" className="flex items-center justify-center gap-2 rounded-full border border-stone-900 py-3.5 text-sm">
          <Instagram className="h-4 w-4" /> Copy & open Instagram
        </a>
        <button data-testid="check-bio-button" onClick={check} disabled={busy} className="ig-gradient flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-medium text-white disabled:opacity-70">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking… (up to a minute)</> : "Check my bio"}
        </button>
      </div>
      {hint && <p data-testid="check-bio-result" className="mt-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">{hint}</p>}
      <p data-testid="pending-status" className="mt-5 text-center text-xs text-stone-500">
        Private account? Switch it to public, tap Check my bio, then switch back to private once you're verified.
      </p>
      <button data-testid="join-restart" onClick={onRestart} className="mt-3 w-full text-xs text-stone-500 underline">Wrong handle? Start over</button>
    </div>
  );
};

export default function Join() {
  const { user, claim, setClaim, refresh } = useAuth();
  const [restart, setRestart] = useState(false);
  const pending = claim?.status === "pending" && !restart;
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [pending, refresh]);
  if (user) return <Navigate to="/dashboard" replace />;
  return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center sm:px-6">
      <ShieldCheck className="mx-auto h-8 w-8 text-pink-500" />
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl">Your Instagram is your account</h1>
      <p className="mt-4 text-stone-600">No passwords. Enter your handle, put a short code in your Instagram bio, and you're verified automatically.</p>
      {claim?.status === "rejected" && !restart && <p data-testid="claim-rejected" className="mt-6 rounded-xl bg-rose-50 p-4 text-sm text-rose-700">We couldn't confirm @{claim.username}. Please try again.</p>}
      {pending
        ? <PendingCard claim={claim} onRestart={() => setRestart(true)} onVerified={refresh} />
        : <JoinForm onDone={(c) => { setClaim(c); setRestart(false); }} />}
      <p className="mt-8 text-xs text-stone-500">Just booking a set? You don't need to join — browse and DM any tech.</p>
    </div>
  );
}
